import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Layers, Lightbulb, TrendingUp } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
const READOUT_AREAS = [{
  icon: TrendingUp,
  title: 'Campaign performance',
  desc: 'Views, chapter completions and CTA click-through, by channel and by teaser'
}, {
  icon: Eye,
  title: 'View depth',
  desc: 'Where viewers pause, replay or drop off within each chapter'
}, {
  icon: Layers,
  title: 'Content & topic analysis',
  desc: 'Which chapters, CHE topics and KOL hooks resonate most with Medical audiences'
}, {
  icon: Lightbulb,
  title: 'Recommendations',
  desc: 'What to double down on, localize next, or turn into a spinoff campaign'
}];
const CHAPTER_DEPTH = [{
  label: 'Unravelling Chronic Hand Eczema',
  value: 82
}, {
  label: 'Living with Chronic Hand Eczema',
  value: 64
}, {
  label: 'Best practices in Chronic Hand Eczema',
  value: 47
}];
export function SlidePerformanceInsights({
  index
}: SlideProps) {
  return <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-[var(--surface)] p-[clamp(2rem,4vw,4rem)]" data-id="element-147" data-mp="219u45z0fmmhs">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.5rem,2.5vw,2.5rem)]" data-id="element-148" data-mp="2b1gqaqeb65zb">
        <p className="mb-[clamp(0.5rem,1vw,1rem)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider text-[var(--brand-green)]" data-id="element-149" data-mp="1icj78l29w55i">
          4. 3-Month Performance Readout
        </p>
        <h2 className="text-[clamp(2rem,3.5vw,3rem)] font-bold leading-[1.1] text-[var(--fg)]" data-id="element-150" data-mp="1sbij0cic4gwq">
          What the first 90 days{' '}
          <span className="text-[var(--brand-green-light)]" data-id="element-151" data-mp="1naks9bj9eejm">will tell you</span>.
        </h2>
      </motion.div>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)]" data-id="element-152" data-mp="33okqmi2ghw4r">
        {/* Left: readout areas */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.35,
        duration: 0.6
      }} className="col-span-6 flex flex-col rounded-2xl border-t-8 border-[var(--brand-green)] bg-white p-[clamp(1.5rem,2.2vw,2.2rem)] shadow-sm" data-id="element-153" data-mp="35uizfumj9gnh">
          <h3 className="mb-4 text-[clamp(1.1rem,1.5vw,1.4rem)] font-bold text-[var(--brand-green)]" data-id="element-154" data-mp="3ksbbudws9oxg">
            Delivered as one reporting session
          </h3>
          <ul className="flex-1 space-y-3.5" data-id="element-155" data-mp="2n1wwkqd7kpte">
            {READOUT_AREAS.map((area, i) => {
            const Icon = area.icon;
            return <li key={i} className="flex items-start gap-3" data-id="element-156" data-mp="2x15vlxmxkumm" data-mpi={i}>
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--brand-green)]/10" data-id="element-157" data-mp="31snm1rrbvctc">
                    <Icon className="h-4 w-4 text-[var(--brand-green)]" data-id="element-158" data-mp="24cgtbva52j9a" />
                  </span>
                  <span className="text-[clamp(0.9rem,1.05vw,1.05rem)] leading-snug text-[var(--fg)]" data-id="element-159" data-mp="29bl06f9lc4wg">
                    <span className="font-semibold" data-id="element-160" data-mp="qggvblzxi1lz">{area.title}</span> —{' '}
                    {area.desc}
                  </span>
                </li>;
          })}
          </ul>
        </motion.div>

        {/* Right: illustrative view-depth chart */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="col-span-6 flex flex-col rounded-2xl bg-[var(--brand-green)] p-[clamp(1.5rem,2.2vw,2.2rem)] text-white shadow-xl" data-id="element-161" data-mp="chz8dqhukt54">
          <p className="mb-1 text-sm font-semibold uppercase tracking-wider text-[var(--brand-green-lighter)]" data-id="element-162" data-mp="3uv6vzec7ph1t">
            View Depth by Chapter
          </p>
          <p className="mb-6 text-xs text-white/60" data-id="element-163" data-mp="3puiutzu9rjtv">
            Illustrative example — actual figures depend on real viewing data
          </p>

          <div className="flex-1 space-y-5" data-id="element-164" data-mp="3fxxrrk1t8iso">
            {CHAPTER_DEPTH.map(chapter => <div key={chapter.label} data-id="element-165" data-mp="1jurlo4a4qsm1">
                <div className="mb-1.5 flex items-center justify-between text-[clamp(0.8rem,1vw,0.95rem)]" data-id="element-166" data-mp="1jvt7n3soozij">
                  <span className="font-medium text-white/90" data-id="element-167" data-mp="1otmc9sec9lgp">
                    {chapter.label}
                  </span>
                  <span className="font-bold tabular-nums text-[var(--brand-green-lighter)]" data-id="element-168" data-mp="1f4kz09z6hi6f">
                    {chapter.value}%
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/15" data-id="element-169" data-mp="113idbyc3i9w1">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${chapter.value}%`
              }} transition={{
                delay: 0.7,
                duration: 0.8,
                ease: 'easeOut'
              }} className="h-full rounded-full bg-white" data-id="element-170" data-mp="165hc0uww4imn" />
                </div>
              </div>)}
          </div>

          <div className="mt-6 h-px bg-white/20" data-id="element-171" data-mp="2qjzwdevauc22" />
          <p className="mt-4 text-[clamp(0.85rem,1vw,0.95rem)] text-white/85" data-id="element-172" data-mp="2qhnd8ydt1vnf">
            Average completion, drop-off points and topic resonance are
            compiled into a single readout with clear next-step
            recommendations.
          </p>
        </motion.div>
      </div>

      {/* Bottom statement */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.9,
      duration: 0.6
    }} className="mt-[clamp(1.25rem,2vw,2rem)] flex items-end justify-between" data-id="element-173" data-mp="jvhafsivrity">
        <p className="text-[clamp(1rem,1.25vw,1.25rem)] font-semibold text-[var(--fg)]" data-id="element-174" data-mp="tm29fh2gupde">
          Delivered once, at the 3-month mark — built from the engagement data
          already captured in scope.
        </p>
      </motion.div>
    </div>;
}